import React, {useState, useEffect} from 'react';
import {Pressable, View, ScrollView, ActivityIndicator} from 'react-native';
import CCText from '@/Atoms/CCText';
import CCLoader from '@/Atoms/CCLoader';
import {useRedeemInvite} from '@/Hooks/ClubsApi/useClubsMutations';
import {useInvitePeek} from '@/Hooks/ClubsApi/useClubsQueries';
import {useMediaQuery} from '@/Hooks/useMediaQuery/useMediaQuery';
import ClubBadge from './components/ClubBadge';
import ErrorBanner from '@/Components/ErrorBanner';
import InviteCountdown from './components/InviteCountdown';
import {BADGES} from './badgeOptions';
import type {PieceKey} from './clubPieces';

const pieceFor = (badgeId?: string): PieceKey =>
  (BADGES.find(b => b.id === badgeId)?.piece ?? 'pawn') as PieceKey;

const redeemErrorMessage = (code: string) => {
  const messages: Record<string, string> = {
    already_member: "You're already a member of this club.",
    revoked: 'This invite has been revoked. Ask a club admin for a new one.',
    exhausted: 'This invite has reached its maximum number of uses.',
    expired: 'This invite has expired. Ask a club admin for a new one.',
    invalid: 'This invite is no longer valid.',
    unauthorized: 'Your session has expired. Please sign in and try again.',
  };
  return (
    messages[code] ??
    "We couldn't join the club. Check your connection and try again."
  );
};

// Live countdown to an ISO expiry. Returns remaining ms + a "23h 41m 08s"-style
// label (drops leading zero units), and re-renders every second.
function useCountdown(expiresAt?: string) {
  const target = expiresAt ? new Date(expiresAt).getTime() : 0;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!expiresAt) {
      return;
    }
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [expiresAt]);
  const remaining = Math.max(0, target - now);
  const total = Math.floor(remaining / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  let label: string;
  if (h > 0) {
    label = `${h}h ${pad(m)}m ${pad(s)}s`;
  } else if (m > 0) {
    label = `${m}m ${pad(s)}s`;
  } else {
    label = `${s}s`;
  }
  return {remaining, label, seconds: s};
}

export default function InviteRedeem({navigation, route}: any) {
  const {code} = route.params;
  const peek = useInvitePeek(code);
  const redeem = useRedeemInvite();
  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);
  const {isMobileOrTab} = useMediaQuery();
  const isWide = !isMobileOrTab;
  // Live countdown to the invite's expiry (hook must run before any early return).
  const {
    remaining,
    label: countdown,
    seconds: countdownSecs,
  } = useCountdown(peek.data?.expires_at);

  const onJoin = async () => {
    setJoining(true);
    setJoinError(null);
    try {
      const res: any = await redeem.mutateAsync(code);
      navigation.navigate('ClubRoom', {clubId: res.club_id});
    } catch (e: any) {
      // A join-time failure (e.g. filled up between peek and join) maps to a code.
      setJoinError(
        e?.response?.data?.error ??
          e?.response?.data?.code ??
          (e?.response?.status === 401 ? 'unauthorized' : 'unknown_error'),
      );
    } finally {
      setJoining(false);
    }
  };

  if (peek.isLoading) {
    return <CCLoader loading={true} />;
  }

  const data = peek.data;
  // Join-time errors override the peeked state (e.g. became full); a countdown
  // that has run out flips a valid invite to expired without a refetch.
  const timedOut =
    data?.state === 'valid' && !!data?.expires_at && remaining <= 0;
  const state =
    joinError === 'club_full' || joinError === 'full'
      ? 'full'
      : ['expired', 'revoked', 'exhausted'].includes(joinError ?? '') ||
        timedOut
      ? 'expired'
      : joinError === 'invalid'
      ? 'invalid'
      : data?.state ?? 'invalid';
  const club = data?.club;

  let content: React.ReactNode;

  if (state === 'valid' && club) {
    // ---- Landing: club preview + Join (design S7 landingOk) ----
    content = (
      <View style={{alignItems: 'center'}}>
        <ClubBadge
          piece={pieceFor(club.badge_id)}
          color={club.background_color || '#3DAB9E'}
          size={88}
          radius={24}
        />
        <CCText style={{fontSize: 12, color: '#888280', marginTop: 8}}>
          You've been invited to join
        </CCText>
        <CCText
          fontWeight="thunder"
          style={{fontSize: 28, color: '#262322', marginTop: 2}}>
          {club.name}
        </CCText>
        {club.description ? (
          <CCText
            style={{
              fontSize: 14,
              color: '#4D3F37',
              lineHeight: 21,
              textAlign: 'center',
              maxWidth: 280,
              marginTop: 6,
            }}>
            {club.description}
          </CCText>
        ) : null}
        <CCText style={{fontSize: 12, color: '#888280', marginTop: 6}}>
          {club.member_count} member{club.member_count !== 1 ? 's' : ''}
        </CCText>
        <Pressable
          testID="invite-join"
          onPress={onJoin}
          disabled={joining}
          style={{
            marginTop: 16,
            width: '100%',
            backgroundColor: '#FACF47',
            borderRadius: 32,
            paddingVertical: 15,
            alignItems: 'center',
          }}>
          {joining ? (
            <ActivityIndicator color="#4D3F37" />
          ) : (
            <CCText style={{color: '#4D3F37', fontWeight: '600', fontSize: 16}}>
              Join {club.name}
            </CCText>
          )}
        </Pressable>
        {joinError ? (
          <ErrorBanner
            testID="invite-redeem-error"
            message={redeemErrorMessage(joinError)}
            style={{marginTop: 10}}
          />
        ) : null}
        {data?.expires_at ? (
          <View testID="invite-countdown">
            <InviteCountdown
              remaining={remaining}
              seconds={countdownSecs}
              label={countdown}
            />
          </View>
        ) : (
          <CCText
            testID="invite-countdown"
            style={{fontSize: 11.5, color: '#A8A7A6', marginTop: 8}}>
            Invite valid for 24 hours
          </CCText>
        )}
      </View>
    );
  } else {
    // ---- Expired / Full / Invalid states (design S7 landingExpired / landingFull) ----
    const view =
      state === 'full'
        ? {
            icon: '♟',
            bg: '#EDE6D2',
            head: 'This club is full',
            body: 'Clubs cap at 50 members. This one just filled up — try another, there are plenty to explore.',
          }
        : {
            icon: '⏳',
            bg: '#FFEBEE',
            head:
              state === 'invalid'
                ? 'Invite not found'
                : 'This invite expired 😕',
            body:
              state === 'invalid'
                ? redeemErrorMessage(joinError ?? 'invalid')
                : joinError
                ? redeemErrorMessage(joinError)
                : 'Invite links are valid for 24 hours. Ask for a new link, or browse other clubs.',
          };
    content = (
      <View style={{alignItems: 'center'}}>
        <View
          style={{
            width: 88,
            height: 88,
            borderRadius: 24,
            backgroundColor: view.bg,
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 8,
          }}>
          <CCText style={{fontSize: 44}}>{view.icon}</CCText>
        </View>
        <CCText
          fontWeight="thunder"
          style={{
            fontSize: 26,
            color: '#262322',
            marginTop: 8,
            textAlign: 'center',
          }}>
          {view.head}
        </CCText>
        <CCText
          style={{
            fontSize: 14,
            color: '#888280',
            lineHeight: 21,
            textAlign: 'center',
            maxWidth: 270,
            marginTop: 8,
            marginBottom: 14,
          }}>
          {view.body}
        </CCText>
        <Pressable
          testID="invite-browse"
          onPress={() => navigation.navigate('Clubs')}
          style={{
            width: '100%',
            backgroundColor: '#3DAB9E',
            borderRadius: 32,
            paddingVertical: 15,
            alignItems: 'center',
          }}>
          <CCText style={{color: '#fff', fontWeight: '600', fontSize: 16}}>
            Browse clubs
          </CCText>
        </Pressable>
      </View>
    );
  }

  if (isWide) {
    return (
      <View
        style={{
          flex: 1,
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#FAF6EB',
          padding: 24,
        }}>
        <View
          style={{
            width: '100%',
            maxWidth: 420,
            backgroundColor: '#fff',
            borderRadius: 20,
            paddingVertical: 36,
            paddingHorizontal: 32,
            borderWidth: 1,
            borderColor: '#E0DDD9',
          }}>
          {content}
        </View>
      </View>
    );
  }

  return (
    <ScrollView
      style={{flex: 1, backgroundColor: '#FAF6EB'}}
      contentContainerStyle={{
        flexGrow: 1,
        justifyContent: 'center',
        paddingHorizontal: 26,
        paddingVertical: 30,
      }}>
      {content}
    </ScrollView>
  );
}
